import axios from "axios";

const BASE_API_URL = "/api/general";

//公開查詢：不需登入的通用 API
class GeneralService {
  //G-1 健康檢查
  health() {
    return axios.get(BASE_API_URL + "/health");
  }

  //G-2 出生地搜尋
  //  回 [{ label, lat, lng }]，label 是完整路徑（「臺灣 彰化縣 彰化市」）。
  //  經緯度是排盤要的 —— 真太陽時靠經度換算。
  birthplaceSearch(q) {
    return axios.get(BASE_API_URL + "/birthplace-search", { params: { q } });
  }

  //G-3 四柱排盤
  //  回 { hasHour, pillars: ["乙亥年", "戊子月", "戊寅日", "庚申時"] }。
  //  不知道時辰就只回三柱 —— 時柱是後端用中午硬湊的，不能秀給使用者看。
  //
  //  排盤放後端而不是前端算：四柱牽涉立春換年、節氣換月、子時跨日、
  //  真太陽時校正，而且這四柱必須跟之後那份報告裡的完全一致。
  //
  //  payload: { birthDate, birthTime, birthPlace, gender }（就是問診收到的那幾欄）
  baziPillars(payload) {
    return axios.post(BASE_API_URL + "/bazi-pillars", payload);
  }

  //G-3b 紫微排盤（鏡月故事頁用）
  //
  //  ⚠️ 不要拿上面那支 baziPillars 給鏡月用 ——
  //  紫微是農曆正月初一換年、八字是立春換年，中間那幾天的年柱不一樣
  //  （實測 2024/02/06：八字甲辰、紫微癸卯）。
  //  後端那支的註解有完整說明。
  ziweiPillars(payload) {
    return axios.post(BASE_API_URL + "/ziwei-pillars", payload);
  }

  //G-4 白夜報告的章節表（公開）
  //  章名與小節名的唯一真相在後端（engines/baiye-natal/chapters）——
  //  那份清單同時是給 AI 的產出規格，前端再存一份就會分叉。
  baiyeChapters() {
    return axios.get(BASE_API_URL + "/baiye-chapters");
  }

  //G-3b 鏡月的章節表。理由與形狀同上一支 ——
  //  唯一真相在 engines/ziwei-jingyue/chapters.js。
  jingyueChapters() {
    return axios.get(BASE_API_URL + "/jingyue-chapters");
  }

  //G-4 白夜故事頁的四塊命書面板
  //  回 { situation, daYun, caiYunPast, aiQing }，每個是字串陣列（一段一筆）。
  //
  //  同步的，約 5 秒。要在問診填完的那一刻就發 ——
  //  後面還有兩支影片（白夜看竹冊 9.5 秒 + 批命 8 秒，都不能跳過），
  //  時間會被那段劇情吸收掉，使用者不會看到任何等待。
  //
  //  payload 就是整份 intake（排盤要生辰，寫文案要他填的煩惱與現況）。
  baiyeTeaser(payload) {
    return axios.post(BASE_API_URL + "/baiye-teaser", payload);
  }

  //G-5 鏡月故事頁的四塊命書面板
  //  回 { situation, daXian, caiYun, aiQing }，每個是字串陣列（一段一筆）。
  //
  //  同步的，實測約 9 秒。發的時機跟白夜那支不一樣 ——
  //  白夜是問診填完就發，鏡月是**故事頁一掛載就發**，
  //  因為她讀盤那兩幕（jingyue-4 / jingyue-5）在故事頁的開場，不在問診頁。
  //  那兩支各約 10 秒、都不能跳過，等於 20 秒的掩護，
  //  使用者再捲到第一塊面板（整頁的 22%）之前絕對回得來。
  //
  //  payload 就是整份 intake（排盤要生辰與時辰，寫文案要他填的煩惱與現況）。
  jingyueTeaser(payload) {
    return axios.post(BASE_API_URL + "/jingyue-teaser", payload);
  }

  //G-7 緋紅的章節表
  //  ⚠️ **一定要帶 line**，而且那個 line 要取自報告內容裡存的值
  //  （report.content.line），不是現場從問診資料重算的 ——
  //  緋紅有三份章節表，卷數不一樣（6 卷 18 節 / 7 卷 21 節），
  //  拿錯一份報告頁會少畫一整卷，而且畫面上看不出哪裡不對。
  feihongChapters(line) {
    return axios.get(BASE_API_URL + "/feihong-chapters", { params: { line } });
  }

  //G-8 公開評價（首頁與 /reviews）
  //  回 [{ id, email, rating, content, timeAgo, repeatCount, productID }]。
  //  只含後台按過「上架」的那些 —— 使用者送出的評價預設不公開。
  //
  //  信箱在後端就遮好了（只留前兩碼）。不要改成前端遮 ——
  //  那樣完整信箱還是會出現在網路回應裡，打開 DevTools 就看得到。
  //
  //  productID 要自己用 constant/products 查成顯示資料（圖、主色、路徑）：
  //  那份是前端的唯一真相，後端只給代號。hooks/useReviews 已經做了這件事。
  reviews(limit) {
    return axios.get(BASE_API_URL + "/reviews", { params: { limit } });
  }

  //G-6 緋紅故事頁的命書面板
  //  ⚠️ 塊數會變：單身線回 { situation, destiny, peach, person }，
  //  尚未交往／已交往線回 { situation, destiny, bond }。
  //  每個是字串陣列（一段一筆）。前端不必判 —— 故事頁的面板表
  //  用 bodyFrom 去查，查不到那一塊就整塊不掛。
  //
  //  同步的。發的時機是**按下開場那顆「開始解命」**（不是掛載就發）——
  //  那是一個明確的意願訊號，在第一支影片中途離開的人就不會被算一次成本。
  //  按下去之後還有 feihong-5（9 秒，不能跳過）＋ 捲到第一塊面板
  //  （整頁的 15~16%），掩護 18~28 秒。
  //
  //  payload 是整份 intake。合盤線要帶對方的生辰，
  //  後端會依 intent/status 自己判要不要排第二張盤。
  feihongTeaser(payload) {
    return axios.post(BASE_API_URL + "/feihong-teaser", payload);
  }
}

//單例：全站共用同一個實例，避免每個元件各自 new 一份
const generalService = new GeneralService();
export default generalService;
